import { CommonModule } from '@angular/common';
import { Component, inject, NgZone } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AuthenticationService } from '../services/authentication.service';
import { AppService } from '../services/app.service';
import { UiSecretsService } from '../services/ui-secrets.service';
import { ToastrService } from 'ngx-toastr';
import { ThemeService } from '../services/theme.service';
import {
  hasSeenKmsiNudge,
  markKmsiNudgeSeen,
  KMSI_NUDGE_MESSAGE,
} from '../helpers/kmsi-nudge.util';
import {
  clearTempPasswordExpiration,
  isTempPasswordExpired,
} from '../helpers/temp-password.util';
import {
  setStoredUser,
  SESSION_ENDED_MESSAGE,
  takeSessionEndedNotice,
} from '../helpers/user-storage.util';
import { resolveReturnUrl, sanitizeReturnUrl } from '../helpers/return-url.util';
import { environment } from '../../environments/environment';

/** What another page may hand login in router navigation state. */
export interface LoginNavigationState {
  email?: string;
  message?: string;
}

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent {
  isValid: boolean = true;
  isSubmitting = false;
  passwordRegx: RegExp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
  emailRegx: RegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  redirectUrl: string = '';
  showPassword: boolean = false;

  constructor(
    public router: Router,
    private route: ActivatedRoute,
    private authService: AuthenticationService,
    private appService: AppService,
    private uiSecretsService: UiSecretsService,
    private toastr: ToastrService,
    private ngZone: NgZone,
    private themeService: ThemeService
  ) {
    this.appService.getMessage.subscribe((message) => {
      if (message != '') {
        this.toastr.success(message);
        this.appService.setMessage('');
      }
    });
    this.appService.redirectUrl.subscribe((url) => (this.redirectUrl = url));

    // The PA site-visit notification email links here with `?returnUrl=` set to the visit's
    // project overview page (Task 7a), so a sign-in from that link lands on the visit instead of
    // the dashboard. This is the same AppService.redirectUrl channel AuthGuard already populates
    // when it bounces an unauthenticated deep link to a guarded route through here - the only new
    // piece is a second producer, reading the value off the URL instead of off the attempted
    // route. sanitizeReturnUrl is what makes that safe: this value came in on a URL, which is not
    // a trusted channel, so an absolute or scheme-bearing value is dropped rather than followed.
    const returnUrl = sanitizeReturnUrl(this.route.snapshot.queryParamMap.get('returnUrl'));
    if (returnUrl) {
      this.appService.setRedirectUrl(returnUrl);
    }
    this.applyNavigationState();
    this.showSessionEndedNotice();
  }

  ngOnInit() {}

  // The set-password page sends the account's email and its success message in
  // navigation state, never a query string, so the address stays out of URLs.
  // Read in the constructor: the navigation has usually ended by ngOnInit.
  private applyNavigationState() {
    const state = this.router.getCurrentNavigation()?.extras.state as
      | LoginNavigationState
      | undefined;
    if (typeof state?.email === 'string' && state.email) {
      this.loginForm.patchValue({ email: state.email });
    }
    if (typeof state?.message === 'string' && state.message) {
      this.toastr.success(state.message);
    }
  }

  // A refused session ends with a full page load, which loses any toast, so the
  // interceptor leaves a one-shot note that is shown and removed here.
  private showSessionEndedNotice() {
    if (takeSessionEndedNotice()) {
      this.toastr.error(SESSION_ENDED_MESSAGE);
    }
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  trimPassword() {
    let password = this.loginForm.get('password')?.value;
    if (password) {
      this.loginForm.patchValue({
        password: password.trim(),
      });
    }
  }

  protected loginForm = new FormGroup({
    email: new FormControl('', [
      Validators.required,
      Validators.pattern(this.emailRegx),
    ]),
    password: new FormControl('', [Validators.required]),
    keepMeSignedIn: new FormControl(false),
  });

  onSubmit() {
    // Blur trims too, but pressing Enter in the field submits without a blur.
    this.trimPassword();
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
    }
    if (
      this.loginForm.value.password != '' &&
      this.loginForm.value.email != ''
    ) {
      if (this.loginForm.value.email?.match(this.emailRegx) === null) {
        this.isValid = false;
      }
    }
    if (
      this.loginForm.value.password != '' &&
      this.loginForm.value.password?.match(this.passwordRegx) === null
    ) {
      // A password that fails the complexity rules is very likely the emailed
      // temporary one, so refuse to submit it once it has expired. The
      // expiration lives on its own storage key, written by the
      // forgot-password / reset-password / create-account flows — this check no
      // longer depends on the shape of the cached 'user'.
      if (isTempPasswordExpired()) {
        this.toastr.error('Your temporary password has expired.');
        return;
      }
    }
    if (this.loginForm.valid) {
      if (this.loginForm.value.email && this.loginForm.value.password) {
        this.isSubmitting = true;
        this.authService
          .login(
            this.loginForm.value.email,
            this.loginForm.value.password,
            !!this.loginForm.value.keepMeSignedIn
          )
          .subscribe({
            next: (data) => {
              this.isSubmitting = false;
              // Every refusal arrives as a FAILURE body, the forced-reset cutover's
              // body 403 included: its message says a link has been emailed, so the
              // page shows it and stays put. A temporary password issued from the
              // admin console still answers code 302 and goes to the forced reset.
              if (data.status === 'FAILURE') {
                this.toastr.error(data.message);
              }
              if (data.code === 302) {
                localStorage.setItem('email', this.loginForm.value.email ?? '');
                this.router.navigate(['resetpassword']);
              } else if (
                data.code === 200 &&
                data.status !== 'FAILURE' &&
                data.data?.id
              ) {
                // EVERY post-login side effect lives inside this one gate — store,
                // theme, temp-password clear, KMSI nudge, maps, pendo, navigation.
                // It is not cosmetic, so don't narrow it back down to just the
                // store: the envelope must be a real success (a FAILURE can still
                // carry code 200, see the toastr.error branch above) AND carry a
                // usable profile. Without the payload check, `setStoredUser(undefined)`
                // writes the literal string "undefined" to the 'user' key, which
                // every getStoredUser() reader then fails into an empty profile —
                // exactly the broken-session bug this flow was fixed for.
                setStoredUser(data.data);
                // Apply the just-fetched user's saved theme now — ThemeService
                // resolved at app boot (before this login populated the user),
                // and login is an SPA nav with no reload.
                this.themeService.applyFromStoredUser();

                // Whatever password just worked, the previously issued
                // temporary one is done with; drop its expiration so it can't
                // block a later sign-in.
                clearTempPasswordExpiration();
                this.maybeShowKmsiNudge(
                  !!this.loginForm.value.keepMeSignedIn,
                  data.data.role
                );

                // Internal users have no map-based features; skip the load.
                if (data.data.role !== 'INTERNAL') {
                  this.uiSecretsService.loadGoogleMaps();
                }

                this.ngZone.runOutsideAngular(() => {
                  if (
                    window.pendo &&
                    typeof window.pendo.initialize === 'function'
                  ) {
                    const visitor = {
                      id: environment.env + data.data.id,
                      email: data.data.email,
                      full_name: data.data.firstName + ' ' + data.data.lastName,
                    };
                    window.pendo.initialize({
                      visitor: visitor,
                    });
                  }
                });

                if (data.data.role === 'INTERNAL') {
                  this.router.navigate(['internal']);
                } else if (data.data.newUser) {
                  // Service + zone setup lives on its own page now (lifted out of
                  // the old manage-account tabs); land new vendors there.
                  this.router.navigate(['services-zones']);
                } else {
                  // navigateByUrl, not navigate([...]): redirectUrl is a serialized URL, and both of its
                  // producers can now supply one carrying a query string — AuthGuard stores `state.url`,
                  // and the PA site-visit email's returnUrl deep-links the scheduling queue as
                  // `/scheduling?visitId=...`. navigate() treats its argument as a path segment, so the
                  // `?` would be percent-encoded into the path and the parameter silently lost — the PA
                  // would land on the queue with nothing opened and no sign anything was dropped.
                  //
                  // Both producers read untrusted input, and a legal-but-unparsable value would be
                  // answered by `Router.parseUrl` with the ROOT tree instead of an error — dropping the
                  // just-signed-in PA somewhere neither this branch nor the sanitizer promises. So the
                  // target is resolved first, and anything that will not survive the parse (including the
                  // empty "no redirect" value) takes the same /dashboard route it always did.
                  const target = resolveReturnUrl(this.router, this.redirectUrl);
                  if (target) {
                    this.router.navigateByUrl(target);
                  } else {
                    this.router.navigate(['dashboard']);
                  }
                }
              } else if (data.code === 200 && data.status !== 'FAILURE') {
                // A success envelope with no usable profile. Unreachable against
                // today's backend, but the user must not be left on a reset login
                // form with no explanation — the FAILURE branch above owns the only
                // other toast, and it never fires here. Same wording as the
                // transport-error handler below: from the user's side this IS just
                // a login that didn't work.
                this.toastr.error('An error occurred. Please try again.');
              }
            },
            error: () => {
              this.isSubmitting = false;
              this.toastr.error('An error occurred. Please try again.');
            }
          });
      }
    }
  }

  /**
   * Shows a one-time nudge toward "Keep Me Signed In" after an untrusted
   * (box-unchecked) login, pointing the user at Settings.
   *
   * Gated on a localStorage flag so it appears once ever per browser — never
   * on a trusted login, and never again once dismissed or acted on. Both the
   * toast being dismissed (onHidden, which also fires on its own timeout) and
   * its "Go to Settings" tap (onTap) mark it seen; neither auto-enables KMSI.
   *
   * KMSI is a vendor-only feature — INTERNAL staff are structurally
   * ineligible for trusted sessions (the trust action 403s for them), so they
   * must never see the nudge. A missing or empty role defaults to
   * PROFESSIONAL_ASSOCIATE, same as RoleGuard/ManageGuard.
   */
  private maybeShowKmsiNudge(keepMeSignedIn: boolean, role?: string): void {
    const userRole = role || 'PROFESSIONAL_ASSOCIATE';
    if (
      keepMeSignedIn ||
      hasSeenKmsiNudge() ||
      userRole !== 'PROFESSIONAL_ASSOCIATE'
    ) {
      return;
    }
    const toast = this.toastr.info(KMSI_NUDGE_MESSAGE, undefined, {
      timeOut: 10000,
      extendedTimeOut: 4000,
    });
    toast.onTap.subscribe(() => {
      markKmsiNudgeSeen();
      this.router.navigate(['settings']);
    });
    toast.onHidden.subscribe(() => {
      markKmsiNudgeSeen();
    });
  }

  navigateRegister() {
    this.router.navigate(['createaccount']);
  }

  clearValidations() {
    this.isValid = true;
  }
}
